import { COMPONENTS } from '@/config/pagePaths.generated.js';
import React from 'react';
import { Outlet, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { faLock } from '@fortawesome/free-solid-svg-icons';
import LoadingPage from '../pages/error-pages/LoadingPage.jsx';

import useSelectedGameLifecycle from '../hooks/useSelectedGameLifecycle.js';
import { useHasPermission } from '../hooks/usePermission.js';
import AccessStatusCard from '../components/access/AccessStatusCard.jsx';
import { useHeldRefusal } from './accessHold.js';

/**
 * Route-content gate that blocks OPERATIONAL pages (live-ops, players, AI Studio, …) when the
 * currently-selected scope (org / game / client) is suspended or sunset.
 *
 * Rather than render the page with leaky per-button disabling (which kept letting forms open and
 * then 409), we replace the whole page with a lock card that routes the user to Game Management to
 * reactivate. Mirrors the backend scope-lifecycle freeze; the reactivate action on Game Management
 * is itself RBAC-gated, so non-owners see the status but can't flip it.
 *
 * Behaviour:
 *  - no decision yet (loading, no cached state) → spinner (don't flash the page).
 *  - frozen → lock card.
 *  - otherwise (active, or lookup failed → fail-open) → children / <Outlet/>.
 *
 * NOT a security boundary — the backend rejects writes regardless. This is the UX lockout the
 * product wants (suspended scope = operational pages off-limits to everyone).
 */
export const RequireActiveScope = ({ children }) => {
    const navigate = useNavigate();
    const { frozen, frozenReason, loading, gameState, clientState } = useSelectedGameLifecycle();
    // Only users who can actually reactivate the scope get the shortcut to Game Management — this is
    // the same FULL_ACCESS gate the reactivate action itself uses (resolved for the current scope,
    // so it covers org/game/client owners + co-owners). Everyone else is told to contact an owner.
    const canReactivate = useHasPermission(COMPONENTS.GAMES_ACTIVATE_GAME, 'FULL_ACCESS');
    // Suspended or sunset while this page was open: it is held (the banner says so) rather than swapped for the
    // lock card under the user's unsaved work.
    const location = useLocation();
    const keepPage = useHeldRefusal(location.pathname, frozen,
        (gameState === 'SUNSET' || clientState === 'SUNSET')
            ? 'This game was sunset - its pages are no longer available.'
            : 'This game was suspended - its pages are closed until it is reactivated.');

    // Block with a spinner only while we genuinely have no decision yet — once any state is known
    // (including cached), render the page or the lock without a flash on every navigation.
    if (loading && gameState == null && clientState == null) {
        return <LoadingPage />;
    }

    if (frozen && !keepPage) {
        const isSunset = gameState === 'SUNSET' || clientState === 'SUNSET';
        // This guard replaces the WHOLE page — a frozen operational scope is off-limits, not merely
        // read-only. So the sunset copy must say "no longer accessible" rather than reuse the
        // per-button "read-only" wording `frozenReason` carries for still-viewable surfaces
        // (tooltips/banners on Game Management & Game Knowledge, where the page stays visible).
        const sunsetScopeNoun = gameState === 'SUNSET' ? 'This game' : 'This game client';
        const message = isSunset
            ? `${sunsetScopeNoun} is sunset — its pages are no longer accessible.`
            : frozenReason;
        return (
            // Centered in the content area like the other shared-card surfaces (No game access, Access
            // pending/granted) — mirrors `.no-game-access`'s flex-center + min-height so the card sits in
            // the middle of the page rather than pinned to the top.
            <div style={{
                flex: 1,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '60vh',
                padding: 'var(--space-32)',
            }}>
            <AccessStatusCard
                // Suspended is recoverable → warning (info) tone; sunset is terminal/EOL → danger tone.
                tone={isSunset ? 'danger' : 'info'}
                icon={faLock}
                title={isSunset ? 'This scope is sunset' : 'This scope is suspended'}
                message={message}
                // Reactivation only applies to a recoverable (suspended) scope, and only for someone who
                // can actually flip it. Sunset is terminal, so no CTA — it would contradict the copy.
                action={!isSunset && canReactivate
                    ? { label: 'Go to Game Management', onClick: () => navigate('/admin-settings/game-mgmt/games') }
                    : undefined}
            >
                <p className="access-status-card-text" style={{ opacity: 0.7, fontSize: '0.9rem' }}>
                    {isSunset
                        ? 'Sunset is permanent (end-of-life) — the scope can’t be reactivated.'
                        : (canReactivate
                            ? 'Operations are disabled while it isn’t active. Reactivate it from Game Management.'
                            : 'Operations are disabled while it isn’t active. Reach out to an owner of this game to reactivate it.')}
                </p>
            </AccessStatusCard>
            </div>
        );
    }

    return children;
};

/**
 * Layout-route shorthand — wrap a whole operational children block in one shot:
 *
 *     { element: <RequireActiveScopeOutlet />, children: [ ...operationalRoutes ] }
 *
 * Re-reads whatever context the ENCLOSING route's `<Outlet context={...}>` provided (e.g. the
 * Game Workspace layout's `{gameId, gameName, gameClientId, gameClientName}`) and forwards it to
 * this Outlet's own children — React Router resets outlet context at every `<Outlet>` unless it's
 * explicitly re-passed, so a bare `<Outlet/>` here would silently give nested pages `undefined`
 * instead of the parent layout's context (this broke GameSetup's `useOutletContext()`, leaving it
 * stuck on `loading` forever since its fetch effect early-returns without a `gameClientId`).
 */
export const RequireActiveScopeOutlet = () => {
    const parentContext = useOutletContext();
    return (
        <RequireActiveScope><Outlet context={parentContext} /></RequireActiveScope>
    );
};

export default RequireActiveScope;
